import React, { useState, useEffect, useCallback } from "react"; // Import useCallback
import { useParams, useNavigate } from 'react-router-dom';
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { CircularProgress, Box, Typography } from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getWithTokenAPI, postWithTokenAPI, putAPI } from "../../utils/Api";

function AdminDetailsPage() {
  const { adminId } = useParams();
  const navigate = useNavigate();

  const [isAdminNew, setIsAdminNew] = useState(adminId === 'new');
  const [isEditing, setIsEditing] = useState(adminId === 'new');
  const [rolesdata, setrolesdata] = useState([])
  const [adminDetails, setAdminDetails] = useState({
    name: "",
    phone_no: "",
    email: "",
    address: "",
    gender: "", 
    date_of_birth: "", 
    password: "", 
    status: "Active",
    role_id: ""
  });

  const permissions = JSON.stringify(localStorage.getItem("permissions"))
  const [loading, setLoading] = useState(true);
  const [formErrorMsg, setFormErrorMsg] = useState("");
  const fetchRoledata = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await getWithTokenAPI(token, "/rolemanagement/find-all");
      if (response.statusCode === 200) {
        const formattedRoles = response.data.roles.map(role => ({
          role_id: role._id,
          role_name: role.role_name
        }));

        setrolesdata(formattedRoles);
      } else {
      }
    } catch (error) {
      console.error("API Error:", error);
    }
  };
  const fetchAdminDetails = useCallback(async () => {
    if (!isAdminNew) {
      setLoading(true);
      setFormErrorMsg("");
      const token = localStorage.getItem("token");
      try {
        const res = await getWithTokenAPI(token, `/admin/find-particular?_id=${adminId}`);

        if (res.statusCode === 200) {
          const { name, phone_number, email, address, gender, dob, status, role_id } = res.data || {};
          setAdminDetails({
            name: name || "",
            phone_no: phone_number || "", 
            email: email || "",
            address: address || "",
            gender: gender || "",
            date_of_birth: dob ? new Date(dob).toISOString().split('T')[0] : "", 
            status: status || "Active",
            password: "", 
            role_id: role_id || ""

          });

        } else {
          toast.error(res.message || "Failed to load admin details.");
          navigate('/backoffice/adminmanagement');
        }
      } catch (error) {
        console.error("Fetch admin details error:", error);
        toast.error("Failed to load admin details due to a network error.");
        navigate('/backoffice/adminmanagement');
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, [adminId, isAdminNew, navigate]); 

  useEffect(() => {
    fetchAdminDetails();
    fetchRoledata()
  }, [fetchAdminDetails]); 

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAdminDetails(prev => ({ ...prev, [name]: value }));
    setFormErrorMsg(""); 
  };

  const handleStatusChange = (e) => {
    setAdminDetails(prev => ({ ...prev, status: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormErrorMsg(""); 
    setLoading(true);
    const token = localStorage.getItem("token");

    if (!adminDetails.name.trim()) {
      setFormErrorMsg("Admin name is required.");
      setLoading(false);
      return;
    }
    if (!adminDetails.phone_no.trim()) {
      setFormErrorMsg("Phone number is required.");
      setLoading(false);
      return;
    }
    if (adminDetails.phone_no.trim().length < 10) {
      setFormErrorMsg("Phone number must be at least 10 digits.");
      setLoading(false);
      return;
    }
    if (!adminDetails.address.trim()) {
      setFormErrorMsg("Address is required.");
      setLoading(false);
      return;
    }
    if (!adminDetails.gender) {
      setFormErrorMsg("Gender is required.");
      setLoading(false);
      return;
    }
    if (!adminDetails.date_of_birth) {
      setFormErrorMsg("Date of Birth is required.");
      setLoading(false);
      return;
    }

    try {
      let res;
      if (isAdminNew) {
        // Logic to create new admin
        if (!adminDetails.email.trim()) {
          setFormErrorMsg("Email is required for new admin.");
          setLoading(false);
          return;
        }
        if (!/\S+@\S+\.\S+/.test(adminDetails.email.trim())) {
          setFormErrorMsg("Invalid email format.");
          setLoading(false);
          return;
        }
        if (!adminDetails.password) {
          setFormErrorMsg("Password is required for new admin.");
          setLoading(false);
          return;
        }
        if (adminDetails.password.length < 6) {
          setFormErrorMsg("Password must be at least 6 characters.");
          setLoading(false);
          return;
        }

        // Payload for CREATE operation
        const createPayload = {
          name: adminDetails.name.trim(),
          email: adminDetails.email.trim(),
          password: adminDetails.password,
          phone_number: adminDetails.phone_no.trim(), // Backend expects 'phone_number'
          address: adminDetails.address.trim(),
          gender: adminDetails.gender,
          dob: adminDetails.date_of_birth, // Backend expects 'dob'
        };
        console.log("Create Admin Payload:", createPayload);
        res = await postWithTokenAPI(token, `/admin/create`, createPayload);
      } else {
        res = await putAPI(token, `/admin/update`, adminDetails);
      }

      if (res.statusCode === 200) {
        toast.success(res.message || `Admin ${isAdminNew ? 'created' : 'updated'} successfully!`, {
          autoClose: 1000,
          onClose: () => {
            // --- MODIFIED LOGIC HERE ---
            // Redirect to adminmanagement page after both create and update
            navigate('/backoffice/adminmanagement');
            // --------------------------
          }
        });
      } else {
        setFormErrorMsg(res.message || `Failed to ${isAdminNew ? 'create' : 'update'} admin.`);
        toast.error(res.message || `Failed to ${isAdminNew ? 'create' : 'update'} admin.`);
      }
    } catch (error) {
      console.error(`Admin ${isAdminNew ? 'creation' : 'update'} error:`, error);
      setFormErrorMsg(`Failed to ${isAdminNew ? 'create' : 'update'} admin due to a network error.`);
      toast.error(`Failed to ${isAdminNew ? 'create' : 'update'} admin due to a network error.`);
    } finally {
      setLoading(false);
    }
  };

  // Show loading only when initially fetching data for existing admin
  // This condition prevents the loading spinner from showing briefly when entering 'new' admin mode
  if (loading && !isAdminNew && (!adminDetails.name && !adminDetails.email)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress style={{ color: "#00b207" }} />
        <Typography sx={{ ml: 2 }}>Loading admin details...</Typography>
      </Box>
    );
  }


  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 style={{color:"#DC3545"}}>{isAdminNew ? "Add New Admin" : "Admin Details"}</h2>
        <div>
          {
            permissions.includes("update_resource") && (
              !isAdminNew && !isEditing && (
                <button
                  className="btn btn-primary me-2"
                  onClick={() => setIsEditing(true)}
                  style={{ backgroundColor: "#DC3545", border: "none" }}
                  disabled={loading}
                >
                  <i className="bi bi-pencil me-2"></i>Edit Admin
                </button>
              )
            )
          }
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/backoffice/adminmanagement')}
            disabled={loading}
          >
            <i className="bi bi-arrow-left me-2"></i>Back to Admins
          </button>
        </div>
      </div>

      <div className="card shadow-sm p-4">
        <form onSubmit={handleSubmit}>
          {formErrorMsg && (
            <div className="alert alert-danger" role="alert">
              {formErrorMsg}
            </div>
          )}
          <div className="row mb-3">
            <div className="col-md-6">
              <label htmlFor="name" className="form-label">Name:</label>
              <input
                type="text"
                className="form-control"
                id="name"
                name="name"
                value={adminDetails.name}
                onChange={handleInputChange}
                readOnly={!isEditing}
                required
              />
            </div>
            <div className="col-md-6">
              <label htmlFor="phone_no" className="form-label">Phone No:</label>
              <input
                type="tel"
                className="form-control"
                id="phone_no"
                name="phone_no"
                value={adminDetails.phone_no}
                onChange={handleInputChange}
                readOnly={!isEditing}
                required
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-6">
              <label htmlFor="email" className="form-label">Email:</label>
              <input
                type="email"
                className="form-control"
                id="email"
                name="email"
                value={adminDetails.email}
                onChange={handleInputChange}
                readOnly={!isEditing} // Email is read-only for existing admins
                required
              />
            </div>
            <div className="col-md-6">
              <label htmlFor="date_of_birth" className="form-label">Date of Birth:</label>
              <input
                type="date"
                className="form-control"
                id="date_of_birth"
                name="date_of_birth"
                value={adminDetails.date_of_birth}
                onChange={handleInputChange}
                readOnly={!isEditing}
                required
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-md-4">
              <label htmlFor="gender" className="form-label">Gender:</label>
              <select
                className="form-select"
                id="gender"
                name="gender"
                value={adminDetails.gender}
                onChange={handleInputChange}
                disabled={!isEditing}
                required
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="mb-3 col-md-4">
              <label className="m-1">Role :</label>
              <select
                name="role_id"
                className="form-select"
                value={adminDetails.role_id}
                onChange={handleInputChange}
                disabled={!isEditing}
              >
                {
                  rolesdata.map((rol) => (
                    <option key={rol.role_id} value={rol.role_id}>
                      {rol.role_name}
                    </option>
                  ))
                }
              </select>
            </div>

            {isAdminNew && ( // Only show password field when creating a new admin
              <div className="col-md-4">
                <label htmlFor="password" className="form-label">Password:</label>
                <input
                  type="password"
                  className="form-control"
                  id="password"
                  name="password"
                  value={adminDetails.password}
                  onChange={handleInputChange}
                  required // Password is required for new admin creation
                />
              </div>
            )}
            {!isAdminNew && ( // Show status dropdown only for existing admins
              <div className="col-md-4">
                <label htmlFor="status" className="form-label">Status:</label>
                <select
                  className="form-select"
                  id="status"
                  name="status"
                  value={adminDetails.status}
                  onChange={handleStatusChange}
                  disabled={!isEditing}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            )}
          </div>
          <div className="mb-3">
            <label htmlFor="address" className="form-label">Address:</label>
            <textarea
              className="form-control"
              id="address"
              name="address"
              rows="3"
              value={adminDetails.address}
              onChange={handleInputChange}
              readOnly={!isEditing}
              required
            ></textarea>
          </div>

          {isEditing && (
            <div className="d-flex justify-content-end">
              <button type="submit" className="btn text-white" style={{ backgroundColor: "#DC3545", fontWeight: "bold" }} disabled={loading} onClick={() => navigate('/backoffice/adminmanagement')}>
                {loading ? <CircularProgress size={24} color="inherit" /> : (isAdminNew ? "Create Admin" : "Save Changes")}
              </button>
              {!isAdminNew && (
                <button type="button" className="btn btn-outline-secondary ms-2" onClick={() => setIsEditing(false)} disabled={loading}>
                  Cancel
                </button>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default AdminDetailsPage;